import { useEffect, useState, useCallback } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/** Admin-only console for reviewing vendor updates & bills that the AI
 * either auto-attached (with undo) or couldn't confidently place (manual). */
export default function AdminVendorReview() {
  const [tab, setTab] = useState("needs_manual"); // needs_manual | auto_attached | settings
  const [pending, setPending] = useState(null);
  const [attached, setAttached] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPending = useCallback(async () => {
    try {
      const r = await api.get("/vendor-review/pending");
      setPending(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Load failed")); }
  }, []);

  const loadAttached = useCallback(async () => {
    try {
      const r = await api.get("/vendor-review/recent-attached?days=14");
      setAttached(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Load failed")); }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get("/vendor-review/settings");
      setSettings(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Load failed")); }
  }, []);

  useEffect(() => { loadPending(); loadAttached(); loadSettings(); }, [loadPending, loadAttached, loadSettings]);

  const undo = async (id) => {
    const reason = window.prompt("Why undo this auto-attach? (optional)", "");
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post(`/vendor-review/attached/${id}/undo`, { reason: reason || "" });
      toast.success("Auto-attach undone. Job's vendor-update surface cleared.");
      await loadAttached();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Undo failed"));
    } finally { setBusy(false); }
  };

  const saveSetting = async (patch) => {
    setBusy(true);
    try {
      const r = await api.post("/vendor-review/settings", patch);
      toast.success("Settings saved");
      setSettings({ ...settings, ...r.data.settings });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  const pendingCount = pending?.count ?? 0;
  const attachedCount = attached?.count ?? 0;

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="font-display font-black text-3xl tracking-tight text-slate-900">Vendor Review</h1>
          <span className="text-[10px] font-black uppercase tracking-widest text-cyan-700 bg-cyan-50 border border-cyan-300 px-2 py-1 rounded">Admin</span>
        </div>
        <p className="text-sm text-slate-600 mb-6">
          The AI auto-attaches high-confidence LCV updates + invoices to the right WO.
          Anything below the threshold lands here for a one-tap fix.
        </p>

        <div className="flex flex-wrap items-center gap-2 border-b-2 border-slate-200 mb-4">
          {[
            { k: "needs_manual", label: `Needs Manual · ${pendingCount}`, testid: "tab-manual" },
            { k: "auto_attached", label: `Auto-Attached · ${attachedCount}`, testid: "tab-attached" },
            { k: "settings", label: "Settings", testid: "tab-settings" },
          ].map(t => (
            <button
              key={t.k}
              data-testid={t.testid}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 text-xs font-black uppercase tracking-widest border-b-2 -mb-[2px]
                ${tab === t.k ? "border-cyan-600 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            >{t.label}</button>
          ))}
        </div>

        {tab === "needs_manual" && (
          <div className="space-y-3" data-testid="pane-manual">
            {!pending && <div className="text-slate-500 font-bold">Loading…</div>}
            {pending && pending.count === 0 && (
              <div className="rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50 p-8 text-center">
                <div className="text-2xl mb-2">🎯</div>
                <div className="font-black text-emerald-800 uppercase tracking-widest text-sm">Nothing to review</div>
                <div className="text-xs text-emerald-700 mt-1">All incoming vendor updates were confidently auto-attached.</div>
              </div>
            )}
            {pending?.items?.map(r => (
              <div key={r.id} className="p-4 bg-white border-2 border-amber-200 rounded-lg" data-testid={`pending-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-black uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                        {r.kind === "vendor_bill" ? "📄 Bill" : "✉️ Update"}
                      </span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{r.source || "unknown"}</span>
                      <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded
                        ${(r.confidence >= 0.7) ? "bg-emerald-100 text-emerald-800"
                          : (r.confidence >= 0.5) ? "bg-amber-100 text-amber-800"
                          : "bg-rose-100 text-rose-800"}`}>
                        {Math.round((r.confidence || 0) * 100)}% conf
                      </span>
                    </div>
                    <div className="mt-1 font-bold text-sm text-slate-900">{r.subject || r.raw_excerpt?.slice(0, 80) || "—"}</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {r.from ? `from ${r.from}` : ""}
                      {r.parsed?.address ? ` · ${r.parsed.address}` : ""}
                      {r.parsed?.price_dollars ? ` · $${r.parsed.price_dollars}` : ""}
                    </div>
                  </div>
                </div>
                {r.matches?.length > 0 && (
                  <div className="mt-3 border-t border-amber-200 pt-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Suggested matches — tap to attach:</div>
                    <div className="space-y-1">
                      {r.matches.slice(0, 3).map((m, i) => (
                        <Link key={i} to={m.job_id ? `/admin/jobs/${m.job_id}` : "#"}
                          className="block text-xs px-2 py-1.5 bg-slate-50 hover:bg-cyan-50 border border-slate-200 hover:border-cyan-400 rounded"
                          data-testid={`match-${r.id}-${i}`}>
                          <span className="font-black uppercase text-[9px] tracking-widest text-cyan-700">
                            {m.kind}{m.wo_number ? ` · WO ${m.wo_number}` : ""}
                          </span>
                          <span className="text-slate-700 ml-2">{m.address}{m.unit ? ` · #${m.unit}` : ""}</span>
                          <span className="text-slate-400 ml-2">{Math.round((m.score || 0) * 100)}%</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                {r.raw_excerpt && (
                  <details className="mt-2">
                    <summary className="text-[10px] font-black uppercase tracking-widest text-slate-500 cursor-pointer">Raw excerpt</summary>
                    <pre className="text-[11px] text-slate-700 mt-1 whitespace-pre-wrap font-mono bg-slate-50 border border-slate-200 rounded p-2">{r.raw_excerpt}</pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "auto_attached" && (
          <div className="space-y-3" data-testid="pane-attached">
            {!attached && <div className="text-slate-500 font-bold">Loading…</div>}
            {attached && attached.count === 0 && (
              <div className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 font-bold">
                No auto-attached vendor updates in the last 14 days.
              </div>
            )}
            {attached?.items?.map(r => (
              <div key={r.id} className="p-4 bg-white border-2 border-emerald-200 rounded-lg" data-testid={`attached-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">✓ auto-attached</span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{r.vendor}</span>
                      <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                        {Math.round((r.confidence || 0) * 100)}% conf
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-slate-900">{r.note}</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      <Link to={`/admin/jobs/${r.job_id}`} className="font-black uppercase tracking-widest text-cyan-700 hover:underline text-[10px]">
                        WO {r.wo_number || "—"}
                      </Link>
                      <span className="ml-2">{r.address}</span>
                      <span className="ml-2 text-slate-400">{new Date(r.attached_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => undo(r.id)}
                    data-testid={`undo-${r.id}`}
                    className="shrink-0 h-8 px-3 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest rounded"
                    title="Undo this auto-attach"
                  >Undo</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="space-y-4 max-w-xl" data-testid="pane-settings">
            <div className="p-4 bg-white border-2 border-slate-200 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!settings.auto_attach_enabled}
                  onChange={e => saveSetting({ auto_attach_enabled: e.target.checked })}
                  data-testid="setting-auto-attach-toggle"
                  className="w-5 h-5 accent-cyan-600"/>
                <div>
                  <div className="font-black uppercase tracking-widest text-sm text-slate-900">Auto-attach LCV updates</div>
                  <div className="text-xs text-slate-600">When email/chat updates match a WO at ≥ the confidence threshold, attach automatically.</div>
                </div>
              </label>
            </div>
            <div className="p-4 bg-white border-2 border-slate-200 rounded-lg">
              <div className="font-black uppercase tracking-widest text-sm text-slate-900 mb-2">Confidence threshold</div>
              <div className="text-xs text-slate-600 mb-2">Only auto-attach above this. Anything below lands in Needs Manual.</div>
              <input type="range" min={0.5} max={1.0} step={0.05}
                value={settings.confidence_threshold}
                onChange={e => saveSetting({ confidence_threshold: parseFloat(e.target.value) })}
                data-testid="setting-conf-threshold"
                className="w-full accent-cyan-600"/>
              <div className="text-xs font-mono font-black text-cyan-700 mt-1">{Math.round((settings.confidence_threshold || 0) * 100)}%</div>
            </div>
            <div className="p-4 bg-white border-2 border-slate-200 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!settings.auto_link_bills_enabled}
                  onChange={e => saveSetting({ auto_link_bills_enabled: e.target.checked })}
                  data-testid="setting-auto-bill-toggle"
                  className="w-5 h-5 accent-cyan-600"/>
                <div>
                  <div className="font-black uppercase tracking-widest text-sm text-slate-900">Auto-link vendor bills</div>
                  <div className="text-xs text-slate-600">On PDF invoice upload, if the top candidate scores ≥ threshold, link the invoice automatically (still shows on this page).</div>
                </div>
              </label>
            </div>
            <div className="p-4 bg-white border-2 border-slate-200 rounded-lg">
              <div className="font-black uppercase tracking-widest text-sm text-slate-900 mb-2">Bill match score threshold</div>
              <div className="text-xs text-slate-600 mb-2">Raw score (0–110) computed from address + unit + date + LCV bonus.</div>
              <input type="range" min={60} max={110} step={5}
                value={settings.bill_score_threshold}
                onChange={e => saveSetting({ bill_score_threshold: parseInt(e.target.value) })}
                data-testid="setting-bill-threshold"
                className="w-full accent-cyan-600"/>
              <div className="text-xs font-mono font-black text-cyan-700 mt-1">{settings.bill_score_threshold}</div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
